import { useCallback, useState } from 'react';
import { message } from 'antd';
import {
    getFlowProjectDetail,
    getFlowTemplateDetail,
    saveFlowTemplate,
    uploadFlowImage,
    applyTemplateToCanvas,
} from '../execution';
import lookTemplate from '../hooks/look.json';
import tryonTemplate from '../hooks/tryon.json';
import {
    buildTemplateCoverPayload,
    renderTemplateCoverToFile,
    buildTemplateClipboardFromDetail,
} from '../utils/template';
import { mergeProjectRecord } from '../utils/project';

/**
 * 画布模板保存/应用管理
 */
export const useCanvasTemplate = ({
    nodesRef,
    edgesRef,
    reactFlowRef,
    canvasRef,
    activeId,
    setProjects,
    ensureCanvasProject,
    cloneClipboardToCanvas,
    setTemplateLibraryOpen,
    getCurrentProjectVersion,
    loadProjectList,
    projectKeywordRef,
    syncProjectVersion,
    restoreProjectCanvas,
    clearGuideQueryParams,
    globalUser,
    member,
}) => {
    const [saveTemplateModalOpen, setSaveTemplateModalOpen] = useState(false);
    const [saveTemplateGroupId, setSaveTemplateGroupId] = useState('');
    const [saveTemplateName, setSaveTemplateName] = useState('');
    const [publishTemplateSync, setPublishTemplateSync] = useState(false);
    const [saveTemplateSubmitting, setSaveTemplateSubmitting] = useState(false);

    const handleOpenSaveTemplateModal = useCallback((groupNodeId = '') => {
        setSaveTemplateGroupId(groupNodeId);
        setSaveTemplateName('');
        setPublishTemplateSync(false);
        setSaveTemplateModalOpen(true);
    }, []);

    const handleCloseSaveTemplateModal = useCallback(() => {
        if (saveTemplateSubmitting) return;
        setSaveTemplateModalOpen(false);
        setSaveTemplateGroupId('');
    }, [saveTemplateSubmitting]);

    const createGroupTemplateCover = useCallback(async (groupNodeId) => {
        const currentNodes = nodesRef.current;
        const groupNodes = currentNodes.filter((node) => node.parentId === groupNodeId);
        const groupNodeIds = new Set(groupNodes.map((node) => String(node.id)));
        const groupEdges = edgesRef.current.filter((edge) => (
            groupNodeIds.has(String(edge.source)) && groupNodeIds.has(String(edge.target))
        ));
        const nodeMap = new Map(currentNodes.map((node) => [String(node.id), node]));

        if (!groupNodes.length) {
            throw new Error('未找到可保存的分组内容');
        }
        const coverPayload = buildTemplateCoverPayload({
            groupNodeId: String(groupNodeId),
            nodes: groupNodes,
            edges: groupEdges,
            nodeMap,
        });
        const file = await renderTemplateCoverToFile(coverPayload, `group-template-${groupNodeId}.png`);
        const coverUrl = await uploadFlowImage(file);

        if (!coverUrl) {
            throw new Error('模板封面上传失败');
        }

        return coverUrl;
    }, [nodesRef, edgesRef]);

    const handleSaveTemplate = useCallback(async () => {
        const projectId = activeId;
        const groupNodeId = saveTemplateGroupId;
        const templateName = (saveTemplateName || '').trim();

        if (!projectId) {
            message.warning('缺少项目 ID，无法保存模板');
            return;
        }
        if (!groupNodeId) {
            message.warning('缺少组合节点 ID，无法保存模板');
            return;
        }
        if (!templateName) {
            message.warning('请填写模板名称');
            return;
        }

        setSaveTemplateSubmitting(true);
        try {
            const cover = await createGroupTemplateCover(groupNodeId);
            await saveFlowTemplate({
                projectId,
                groupNodeId,
                templateName,
                cover,
                toPublic: publishTemplateSync,
            });
            setSaveTemplateModalOpen(false);
            setSaveTemplateGroupId('');
        } catch (error) {
            console.error(error);
        } finally {
            setSaveTemplateSubmitting(false);
        }
    }, [activeId, createGroupTemplateCover, publishTemplateSync, saveTemplateGroupId, saveTemplateName]);

    const handleApplyTemplateToCanvas = useCallback(async (templateItem) => {
        const templateId = String(templateItem?.id || '');
        const reactFlow = reactFlowRef.current;
        const canvas = canvasRef.current;

        if (!templateId) {
            message.warning('缺少模板 ID');
            return false;
        }
        if (!reactFlow || !canvas) return false;

        const projectContext = await ensureCanvasProject();

        try {
            const templateDetail = await getFlowTemplateDetail(templateId);
            const clipboard = buildTemplateClipboardFromDetail(templateDetail);
            const bounds = canvas.getBoundingClientRect();
            const flowPosition = reactFlow.screenToFlowPosition({
                x: bounds.left + bounds.width / 2,
                y: bounds.top + bounds.height / 2,
            });
            const applied = cloneClipboardToCanvas(clipboard, {
                flowPosition,
                projectId: projectContext?.projectId,
            });

            if (applied) {
                setTemplateLibraryOpen(false);
            }

            return applied;
        } catch (error) {
            console.error(error);
            message.error(error?.message || '应用模板失败');
            throw error;
        }
    }, [canvasRef, cloneClipboardToCanvas, ensureCanvasProject, reactFlowRef, setTemplateLibraryOpen]);

    // 应用预设模板并刷新当前项目画布
    const handleSelectTemplate = useCallback(async (templateKey) => {
        const templateConfigMap = {
            faceSwap1: lookTemplate,
            lookImg: tryonTemplate,
        };
        const targetTemplate = templateConfigMap[templateKey];
        if (!targetTemplate) return;

        try {
            const { projectId } = await ensureCanvasProject();

            await applyTemplateToCanvas({
                projectId,
                version: getCurrentProjectVersion(projectId),
                templateNodes: targetTemplate.nodes,
                templateEdges: targetTemplate.edges,
            });

            // 刷新项目列表和项目详情
            loadProjectList({ pageIndex: 1, append: false, keyword: projectKeywordRef.current || '' });
            const projectDetail = await getFlowProjectDetail(projectId);
            syncProjectVersion(projectId, projectDetail.version);
            setProjects((prev) => prev.map((project) => (
                project.id === projectId
                    ? mergeProjectRecord(project, {
                        ...project,
                        name: projectDetail.name || project.name,
                        cover: projectDetail.cover,
                        studioId: projectDetail.studioId,
                        version: projectDetail.version,
                    })
                    : project
            )));
            restoreProjectCanvas(projectDetail);
            clearGuideQueryParams();
        } catch (error) {
            console.error(error);
        }
    }, [clearGuideQueryParams, ensureCanvasProject, getCurrentProjectVersion, loadProjectList, restoreProjectCanvas, syncProjectVersion, setProjects, projectKeywordRef]);

    return {
        saveTemplateModalOpen,
        setSaveTemplateModalOpen,
        saveTemplateGroupId,
        saveTemplateName,
        setSaveTemplateName,
        publishTemplateSync,
        setPublishTemplateSync,
        saveTemplateSubmitting,
        handleOpenSaveTemplateModal,
        handleCloseSaveTemplateModal,
        createGroupTemplateCover,
        handleSaveTemplate,
        handleApplyTemplateToCanvas,
        handleSelectTemplate,
    };
};